import { useContext, useEffect, useMemo, useRef } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AuthContext } from "react-oauth2-code-pkce";

import { useApp } from "../app/Application.provider";
import {
  buildTenantUrl,
  consumeAuthState,
  hasStoredOAuthToken,
  normalizeAuthError,
} from "../auth/utils";
import { SAFE_FALLBACK_ROUTE } from "../auth/constants";
import { Spinner } from "../components/spinner/Spinner";

export const Route = createFileRoute("/")({
  component: RootLanding,
});

function RootLanding() {
  const { auth } = useApp();
  const { loginInProgress, token: oauthToken, error: oauthError } = useContext(AuthContext);
  const navigate = useNavigate();
  const hasNavigatedRef = useRef(false);

  // Detect whether this page load started with an OAuth authorization code in the URL.
  // Captured once at mount — the library clears the URL params, so this must be memoized.
  const isOAuthCallback = useMemo(
    () => new URLSearchParams(window.location.search).has("code"),
    [],
  );

  useEffect(() => {
    // Library is actively exchanging the code — wait.
    if (loginInProgress) {
      hasNavigatedRef.current = false;

      return;
    }

    // Library has a token (in context or sessionStorage) but OAuthBridge hasn't
    // dispatched AUTH_LOGIN yet — wait for state to sync before navigating.
    // This handles race conditions where loginInProgress becomes false before
    // the token context value updates.
    if (!auth.isAuthenticated && (!!oauthToken || hasStoredOAuthToken()) && !oauthError) {
      return;
    }

    if (hasNavigatedRef.current) return;
    hasNavigatedRef.current = true;

    if (oauthError && !auth.isAuthenticated) {
      void navigate({
        to: "/auth/error" as string,
        search: { reason: normalizeAuthError(oauthError) } as Record<string, string>,
        replace: true,
      });
      return;
    }

    if (auth.isAuthenticated) {
      if (isOAuthCallback) {
        // Came from the IdP — go through /auth/callback for explicit UX (Option B).
        void navigate({ to: "/auth/callback" as string, replace: true });
      } else {
        // Normal page load with a stored session — skip /auth/callback, go straight to destination.
        const state = consumeAuthState();
        if (state?.tenant) {
          window.location.href = buildTenantUrl(state.tenant, state.redirect ?? SAFE_FALLBACK_ROUTE);
        } else {
          void navigate({ to: (state?.redirect ?? SAFE_FALLBACK_ROUTE) as string, replace: true });
        }
      }
      return;
    }

    // Not authenticated, no stored token — send to login.
    void navigate({ to: "/auth/login" as string, replace: true });
  }, [loginInProgress, auth.isAuthenticated, oauthToken, oauthError, isOAuthCallback, navigate]);

  return <Spinner fullScreen size="lg" label={isOAuthCallback ? "Authenticating..." : "Loading..."} />;
}
